import { useState } from 'react';

/**
 * MailToolbar
 * – Left side: checkbox, refresh, more options
 * – Middle: sync status banner (running / failed / idle with has_more)
 * – Right side: "N–M of Total" + prev / next page buttons
 */
export function MailToolbar({
  totalCount = 0,
  onRefresh,
  isRefreshing: propIsRefreshing = false,
  hasNextPage = false,
  isFetchingNextPage = false,
  totalApiCount,
  syncInfo = null, // { status, synced_count, total_estimate, has_more }
  onStartSync,
}) {
  const [localRefreshing, setLocalRefreshing] = useState(false);
  const isRefreshing = propIsRefreshing || localRefreshing;

  const handleRefresh = async () => {
    if (isRefreshing) return;
    setLocalRefreshing(true);
    try {
      await onRefresh?.();
    } finally {
      setTimeout(() => {
        setLocalRefreshing(false);
      }, 700);
    }
  };
  const start = totalCount > 0 ? 1 : 0;
  const displayTotal = totalApiCount
    ? totalApiCount.toLocaleString()
    : hasNextPage
      ? 'many'
      : totalCount;
  const paginationText = `${start}–${totalCount} of ${displayTotal}`;

  const isSyncing = syncInfo?.status === 'running';
  const syncFailed = syncInfo?.status === 'failed';
  const hasPending = syncInfo?.has_more && !isSyncing;

  return (
    <div className="flex flex-col border-b border-gray-100 bg-white select-none">
      {/* Sync status banner */}
      {syncInfo && (isSyncing || syncFailed || hasPending) && (
        <div
          className={`flex items-center justify-between px-4 py-1.5 text-xs font-medium ${
            isSyncing
              ? 'bg-blue-50 text-blue-700'
              : syncFailed
                ? 'bg-red-50 text-red-700'
                : 'bg-amber-50 text-amber-700'
          }`}
        >
          <div className="flex items-center gap-2">
            {isSyncing && (
              <svg
                className="w-3.5 h-3.5 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            <span>
              {isSyncing
                ? `Syncing emails… ${syncInfo.synced_count?.toLocaleString() || 0}${syncInfo.total_estimate ? ` of ~${syncInfo.total_estimate.toLocaleString()}` : ''}`
                : syncFailed
                  ? `Sync failed — ${syncInfo.synced_count?.toLocaleString() || 0} emails imported so far`
                  : `${syncInfo.synced_count?.toLocaleString() || 0} emails imported — more available`}
            </span>
          </div>

          {/* Action button */}
          {(syncFailed || hasPending) && onStartSync && (
            <button
              type="button"
              onClick={() => onStartSync('all')}
              className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors ${
                syncFailed
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-amber-600 text-white hover:bg-amber-700'
              }`}
            >
              {syncFailed ? 'Retry Sync' : 'Resume Sync'}
            </button>
          )}
        </div>
      )}

      {/* Main toolbar row */}
      <div className="flex items-center justify-between px-3 sm:px-4 py-2">
        {/* Left controls */}
        <div className="flex items-center gap-2">
          {/* Refresh */}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className={`p-1.5 rounded-lg transition-all active:scale-90 cursor-pointer ${
              isRefreshing
                ? 'text-primary bg-primary/10 cursor-wait'
                : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
            }`}
            title={isRefreshing ? 'Refreshing...' : 'Refresh'}
            aria-label="Refresh"
          >
            <svg
              className={`w-4 h-4 transition-transform duration-300 ${
                isRefreshing ? 'animate-spin text-primary' : ''
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
        </div>

        {/* Right count info */}
        <div className="flex items-center gap-2 text-xs text-gray-500 font-medium">
          {isFetchingNextPage && (
            <svg
              className="w-3.5 h-3.5 animate-spin text-primary"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          )}
          <span>{paginationText}</span>
        </div>
      </div>
    </div>
  );
}
